import React, { useEffect, useState } from 'react';
import { Field, FormSpy } from 'react-final-form';
import Axios from 'axios';
import * as Routes from '@/modules/routes';
import { Button } from '@/components/ui/Buttons';
import Icon from '@/components/ui/Icon';
import Text from '@/components/ui/Text';
import cn from '@/utils/cn';
import styles from '../../Form.module.scss';
import ReactSelectAdapter from './ReactSelectAdapter';

interface School {
  uid: string;
  name: string;
  number?: string;
}

type LoadStatus = 'idle' | 'loading' | 'loaded' | 'empty' | 'failed';

interface SchoolNameSyncProps {
  form: { change: (field: string, value: unknown) => void };
  schools: School[];
  schoolUid?: string;
}

// The name is persisted alongside the uid so the modal can show the saved school without calling Clever again.
const SchoolNameSync = ({ form, schools, schoolUid }: SchoolNameSyncProps) => {
  useEffect(() => {
    if (!schoolUid) {
      form.change('clever_school_name', null);
      return;
    }

    const school = schools.find(({ uid }) => uid === schoolUid);

    if (school) form.change('clever_school_name', school.name);
  }, [form, schools, schoolUid]);

  return null;
};

const statusMessages: Partial<Record<LoadStatus, string>> = {
  empty: 'Clever returned no schools for this district.',
  failed: 'Could not load schools. Check the Clever ID and try again.',
  loading: 'Loading schools…',
};

interface LoadStatusMessageProps {
  status: LoadStatus;
}

const LoadStatusMessage = ({ status }: LoadStatusMessageProps) => {
  const message = statusMessages[status];

  if (!message) return null;

  return (
    <Text className="tw-ml-[90px] tw-mb-0 tw-text-very-dark-grey" size="sm">
      {status === 'loading' && <i aria-hidden="true" className="fa fa-spinner fa-spin tw-mr-1" />}
      {message}
    </Text>
  );
};

interface ArchiveWarningProps {
  schoolName?: string;
}

const ArchiveWarning = ({ schoolName }: ArchiveWarningProps) => (
  <div
    className={cn(
      'tw-ml-[90px] tw-mb-3 tw-mt-2 tw-rounded tw-flex tw-gap-2 tw-py-2 tw-px-4',
      'tw-border tw-border-solid tw-border-orange tw-bg-orange/10',
    )}
  >
    <Icon className="tw-shrink-0 tw-text-orange" icon="Warning" size={21} />
    <div>
      <Text className="tw-mb-2" size="sm" weight="bold">
        Rostering will be limited to {schoolName || 'the selected school'}.
      </Text>
      <Text className="tw-mb-2" size="sm">
        On the next sync, classrooms, teachers, and students on this account that belong to any other school
        will be deactivated. This cannot be undone from this screen.
      </Text>
      <div className="tw-flex tw-items-center">
        <Field
          className="tw-mr-2"
          component="input"
          id="clever_school_acknowledged"
          name="clever_school_acknowledged"
          type="checkbox"
        />
        <Text className="tw-mb-0" htmlFor="clever_school_acknowledged" size="sm" tag="label">
          I understand that data outside this school will be deactivated.
        </Text>
      </div>
    </div>
  </div>
);

interface CleverSchoolProps {
  savedSchoolUid?: string;
  subscriberId: number | string;
}

const CleverSchool = ({ savedSchoolUid, subscriberId }: CleverSchoolProps) => {
  const [schools, setSchools] = useState<School[]>([]);
  const [status, setStatus] = useState<LoadStatus>('idle');

  const loadSchools = async (uid: string) => {
    setStatus('loading');

    try {
      const { data } = await Axios.get(
        Routes.plato_api_clever_schools_subscriber_path(subscriberId),
        { params: { uid }, timeout: 15000 },
      );

      if (data.error) {
        setStatus('failed');
        return;
      }

      setSchools(data.data);
      setStatus(data.data.length ? 'loaded' : 'empty');
    }
    catch (error) {
      setStatus('failed');
    }
  };

  // A change is only destructive if it moves away from what is already saved; re-picking the saved school is not.
  const requiresAcknowledgement = (value?: string) => !!value && value !== savedSchoolUid;

  const validateSchool = (value?: string, allValues?: { clever_school_acknowledged?: boolean }) => {
    if (!requiresAcknowledgement(value)) return undefined;

    return allValues?.clever_school_acknowledged
      ? undefined
      : 'Confirm that data outside this school may be deactivated.';
  };

  return (
    <FormSpy subscription={{ errors: true, values: true }}>
      {({ errors, form, values }) => (
        <>
          <SchoolNameSync form={form} schools={schools} schoolUid={values.clever_school_uid} />

          <div className={styles.row}>
            <div className={styles.fieldContainer}>
              <Text className={styles.label} htmlFor="clever_school_uid" size="sm" tag="label" weight="bold">
                Clever School
              </Text>
              <Field
                className={styles.input}
                component={ReactSelectAdapter}
                disabled={status !== 'loaded'}
                id="clever_school_uid"
                name="clever_school_uid"
                options={schools.map(({ name, uid }) => ({ label: name, value: uid }))}
                parse={val => (val ? val.value : val)}
                placeholder={status === 'loaded' ? 'Select a school' : 'Load schools to select one'}
                validate={validateSchool}
              />
            </div>
          </div>

          <div className={styles.row}>
            <div className={styles.fieldContainer}>
              <Button
                outlineStyle
                small
                className="tw-ml-[90px]"
                disabled={!values.uid || status === 'loading'}
                type="button"
                onClick={() => loadSchools(values.uid)}
              >
                Load Schools
              </Button>
            </div>
          </div>

          <LoadStatusMessage status={status} />

          {requiresAcknowledgement(values.clever_school_uid) && (
            <ArchiveWarning schoolName={values.clever_school_name} />
          )}

          {errors.clever_school_uid && (
            <Text className={styles.fieldError} size="sm">{errors.clever_school_uid}</Text>
          )}
        </>
      )}
    </FormSpy>
  );
};

export default CleverSchool;
